// src/components/LandingPage/Header.jsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
// Path from src/components/LandingPage/ to src/contexts/AuthContext.js
import { useAuth } from "../../contexts/AuthContext";
// Path from src/components/LandingPage/ to src/assets/images/landing/skillMatchLogo.png
import skillMatchLogo from "../../assets/images/landing/skillMatchLogo.png";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isAuthenticated } = useAuth(); // Get authentication status from your AuthContext
  const navigate = useNavigate();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Define navigation links with types and paths
  const navLinksConfig = [
    {
      type: "auth_route",
      label: "Home",
      loggedInPath: "/dashboard", // If logged in, go to dashboard
      loggedOutPath: "/login", // If not logged in, clicking Dashboard goes to login
    },
    {
      type: "anchor",
      label: "FAQs",
      href: "/landingpage#faqs", // Ensure this takes user to landing page's FAQ section
    },
    // --- MODIFICATION START ---
    // Changed "Contact Us" from an anchor link to a page link.
    // This now uses a different type and a simple path.
    {
      type: "page_link",
      label: "Contact Us",
      path: "/contact",
    },
    // --- MODIFICATION END ---
  ];

  const handleLoginClick = () => {
    if (isMobileMenuOpen) toggleMobileMenu();
    navigate("/login");
  };

  const handleGetStartedClick = () => {
    if (isMobileMenuOpen) toggleMobileMenu();
    navigate("/signup");
  };

  const renderNavLink = (link, isMobile = false) => {
    const commonClasses = isMobile
      ? "block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-green-700 hover:bg-gray-50"
      : "text-gray-700 hover:text-green-700 font-medium";

    const handleClick = () => {
      if (isMobile) {
        toggleMobileMenu();
      }
      // For anchor links, if not on landing page, navigate first then scroll
      if (
        link.type === "anchor" &&
        !window.location.pathname.startsWith("/landingpage")
      ) {
        // Check if already on landing page to avoid unnecessary navigation
        if (window.location.pathname !== "/landingpage") {
          navigate(link.href.split("#")[0]); // Navigate to /landingpage
        }
      }
    };

    if (link.type === "auth_route") {
      const path = isAuthenticated() ? link.loggedInPath : link.loggedOutPath;
      return (
        <Link
          key={link.label}
          to={path}
          className={commonClasses}
          onClick={handleClick}
        >
          {link.label}
        </Link>
      );
    } else if (link.type === "anchor") {
      // If we are already on the landing page, simple href is fine.
      // If not, we should navigate to landing page first, then the hash will work.
      if (window.location.pathname.startsWith("/landingpage")) {
        return (
          <a
            key={link.label}
            href={link.href.substring(link.href.indexOf("#"))}
            className={commonClasses}
            onClick={handleClick}
          >
            {link.label}
          </a>
        );
      } else {
        // This Link component will navigate to /landingpage, and the #faqs will be handled by the browser
        return (
          <Link
            key={link.label}
            to={link.href}
            className={commonClasses}
            onClick={handleClick}
          >
            {link.label}
          </Link>
        );
      }
      // --- MODIFICATION START ---
      // Add a new condition to handle simple page links like our new Contact Us page.
    } else if (link.type === "page_link") {
      return (
        <Link
          key={link.label}
          to={link.path}
          className={commonClasses}
          onClick={handleClick}
        >
          {link.label}
        </Link>
      );
      // --- MODIFICATION END ---
    }
    return null;
  };

  return (
    <header className="relative flex items-center justify-between p-4 sm:px-6 md:px-8 lg:px-16 py-6 bg-white shadow-sm">
      <div className="flex items-center">
        {/* Make logo link to landing page */}
        <Link to="/landingpage">
          <img
            src={skillMatchLogo}
            alt="SkillMatch AI Logo"
            className="h-10 w-auto mr-2 cursor-pointer"
          />
        </Link>
      </div>

      <nav className="hidden md:flex items-center md:space-x-6 lg:space-x-12 xl:space-x-16">
        {navLinksConfig.map((link) => renderNavLink(link, false))}
      </nav>

      <div className="hidden md:flex items-center space-x-3">
        <button
          onClick={handleLoginClick}
          className="px-6 py-2 text-sm font-semibold text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        >
          Login
        </button>
        <button
          onClick={handleGetStartedClick}
          className="px-6 py-2 text-sm font-semibold text-white bg-[#073214] rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 flex items-center"
        >
          Get Started
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4 ml-2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
            />
          </svg>
        </button>
      </div>

      {/* Mobile Menu Button (Hamburger/Close) */}
      <div className="md:hidden">
        <button
          onClick={toggleMobileMenu}
          className="text-gray-700 focus:outline-none"
          aria-label="Toggle mobile menu"
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-7 w-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 6h16M4 12h16m-7 6h7"
              />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg z-50">
          <nav className="flex flex-col px-4 pt-4 pb-2 space-y-2">
            {navLinksConfig.map((link) => renderNavLink(link, true))}
          </nav>
          <div className="px-4 py-4 space-y-3 border-t border-gray-200">
            <button
              onClick={handleLoginClick}
              className="w-full px-6 py-2 text-sm font-semibold text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Login
            </button>
            <button
              onClick={handleGetStartedClick}
              className="w-full px-6 py-2 text-sm font-semibold text-white bg-[#073214] rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 flex items-center justify-center"
            >
              Get Started
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-4 h-4 ml-2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
